import type { FileSegment, ImageParser, ParseResult } from './Parser';
import { getDescription } from './JpegDescriptions';
import { ExifUtils } from './ExifUtils';

export type Marker = FileSegment;

export class JpegParser implements ImageParser {
  parse(data: Uint8Array): ParseResult {
    const segments = parseJPEG(data);
    let metadata: Record<string, string> = {};

    // Find APP1 Exif
    const app1 = segments.find(s => s.name === 'APP1');
    if (app1) {
      // payload starts at payloadStart.
      // APP1 payload: Marker(2) + Length(2) + [ExifHeader + TIFF]
      // app1.payloadStart points to data AFTER length.

      // Safety check
      if (app1.payloadStart < data.length) {
        const payload = data.slice(app1.payloadStart, app1.end);
        metadata = ExifUtils.parse(payload);
      }
    }

    return {
      segments,
      format: 'JPEG',
      metadata
    };
  }

  getDescription(name: string): string {
    return getDescription(name);
  }
}

// Ensure generic interface compatibility
export function parseJPEG(data: Uint8Array): FileSegment[] {
  const markers: FileSegment[] = [];
  let i = 0;

  while (i < data.length) {
    if (data[i] === 0xff) {
      const markerByte = data[i + 1];

      if (markerByte === 0x00 || markerByte === 0xff) {
        i++;
        continue;
      }

      const name = getMarkerName(markerByte);
      const start = i;
      let length = 0;
      let payloadStart = i + 2;
      let end = i + 2;

      // Markers that do NOT have a length field
      const noLength =
        [0xd8, 0xd9, 0x01].includes(markerByte) ||
        (markerByte >= 0xd0 && markerByte <= 0xd7);

      if (noLength) {
        length = 0;
        end = i + 2;
        // No Length markers have no payload (structure only)
        payloadStart = end;
      } else {
        if (i + 3 < data.length) {
          length = (data[i + 2] << 8) + data[i + 3];
          end = i + 2 + length;
          payloadStart = i + 4; // Skip marker(2) + length(2)
        }
      }

      // Special handling for Scan Data (SOS)
      if (markerByte === 0xda) {
        let scanIndex = end;
        while (scanIndex < data.length - 1) {
          if (
            data[scanIndex] === 0xff &&
            data[scanIndex + 1] !== 0x00 &&
            (data[scanIndex + 1] < 0xd0 || data[scanIndex + 1] > 0xd7)
          ) {
            break;
          }
          scanIndex++;
        }
        end = scanIndex;
      }

      markers.push({
        index: markers.length,
        type: markerByte, // Use 'type' instead of 'marker'
        name: name,
        start: start,
        payloadStart: payloadStart,
        end: end,
        size: end - start,
      });

      i = end;
    } else {
      i++;
    }
  }
  return markers;
}

function getMarkerName(byte: number): string {
  if (byte >= 0xe0 && byte <= 0xef) return `APP${byte - 0xe0}`;
  if (byte >= 0xd0 && byte <= 0xd7) return `RST${byte - 0xd0}`;
  const map: Record<number, string> = {
    0xd8: 'SOI',
    0xd9: 'EOI',
    0xdb: 'DQT',
    0xc0: 'SOF0',
    0xc2: 'SOF2',
    0xc4: 'DHT',
    0xda: 'SOS',
    0xfe: 'COM',
    0xdd: 'DRI',
  };
  return map[byte] || `FF${byte.toString(16).toUpperCase()}`;
}
